/**
 * Qoryn client capability adapter — v1.
 *
 * A portable, presentation-hint-only view over the server-side
 * capability contract at ``GET /api/client/capabilities``.
 *
 * DESIGN INVARIANTS
 * -----------------
 *
 * 1. This module is a PRESENTATION HINT LAYER. It NEVER substitutes
 *    for server-side authorization on actions. Any UI code that
 *    reads a capability's ``enablement`` / ``readiness`` and uses
 *    it to render an action button must ALSO check the server's
 *    authoritative response when the action is invoked. The server
 *    is the sole authority.
 *
 * 2. UNKNOWN AND UNVERIFIED MUST NEVER YIELD A POSITIVE UI HINT.
 *    - ``enablement !== "not_held"`` → no positive hint.
 *    - ``readiness !== "verified"`` → no positive hint.
 *    - Any transport error, parse error, HTTP non-200, or invalid
 *      / missing required field → no positive hint.
 *
 * 3. TRANSPORT IS INJECTED. The adapter accepts a ``transport`` and
 *    ``session`` context so unit tests and alternate integrations
 *    (KrownBook, headless clients) supply their own request path.
 *    No provider keys, no embedded privileged credentials.
 *
 * 4. CLIENT-SUPPLIED HINTS (client_type, device_id, custom headers)
 *    are ignored server-side and MUST NOT be sent by this adapter.
 *    Only the injected ``Authorization`` bearer is transmitted.
 *
 * 5. Errors return BOUNDED short codes only. No provider text, no
 *    server ``detail`` field, no interpolated request-body values,
 *    and no ``Error.message`` are ever surfaced in adapter output.
 *
 * 6. Malicious / spoofed / malformed server payloads FAIL CLOSED for
 *    the WHOLE fact — the fact is dropped from the returned contract
 *    (not merely coerced to a permissive default that could later
 *    pass classification).
 */

const SUPPORTED_CONTRACT_VERSIONS = new Set(["1"]);

const ENABLEMENT_LITERALS = new Set(["held", "dormant", "not_held", "unknown"]);
const READINESS_LITERALS = new Set(["verified", "unverified"]);
const IMPL_LITERALS = new Set(["known_implemented", "unknown"]);

/**
 * Strict, shared per-fact validator. Every required field must be
 * present with an accepted type/literal. Missing or invalid ⇒ null.
 * Returning null means: this fact fails closed for the WHOLE fact
 * — DO NOT coerce to a permissive default and continue.
 *
 * Required fields:
 *   id                    string, non-empty
 *   label                 string, non-empty
 *   declared_enabled      strict boolean (no coercion from string /
 *                         number / null / undefined)
 *   implementation_status literal in IMPL_LITERALS
 *   enablement            literal in ENABLEMENT_LITERALS
 *   readiness             literal in READINESS_LITERALS
 *
 * Optional fields (accepted only if type matches; otherwise null):
 *   enablement_reason     string | null
 *   scope_note            string | null
 */
function _validateFact(raw) {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
    if (typeof raw.id !== "string" || raw.id.length === 0) return null;
    if (typeof raw.label !== "string" || raw.label.length === 0) return null;
    if (typeof raw.declared_enabled !== "boolean") return null;
    if (!IMPL_LITERALS.has(raw.implementation_status)) return null;
    if (!ENABLEMENT_LITERALS.has(raw.enablement)) return null;
    if (!READINESS_LITERALS.has(raw.readiness)) return null;
    const enablement_reason =
        typeof raw.enablement_reason === "string" ? raw.enablement_reason : null;
    const scope_note =
        typeof raw.scope_note === "string" ? raw.scope_note : null;
    return {
        id: raw.id,
        label: raw.label,
        declared_enabled: raw.declared_enabled,
        implementation_status: raw.implementation_status,
        enablement: raw.enablement,
        enablement_reason,
        readiness: raw.readiness,
        scope_note,
    };
}

/**
 * Shared collection validator. Runs BEFORE per-fact validation so
 * that ambiguous ids are detected across the whole raw collection,
 * not merely across the surviving-post-validation subset. This
 * closes a fail-closed gap where a valid+malformed pair with the
 * same id could survive if the malformed twin were dropped
 * pre-count.
 *
 * Semantics:
 *   1. An id is AMBIGUOUS if it appears >= 2 times among raw
 *      entries whose ``id`` is a string. (Non-string ids are noise
 *      that would fail per-fact validation anyway; they are not
 *      counted.)
 *   2. Every occurrence of an ambiguous id is EXCLUDED from the
 *      valid-facts output, regardless of whether individual
 *      entries would otherwise pass per-fact validation.
 *   3. Ambiguous ids are reported via the ``ambiguousIds`` Set so
 *      ``indexCapabilities`` can retain its documented convention
 *      of ``Map.get(id) === null`` for ambiguous lookup.
 *
 * @param {Iterable<unknown>} rawEntries raw capability entries
 * @returns {{ facts: object[], ambiguousIds: Set<string> }}
 */
function _collectValidFacts(rawEntries) {
    // Step 1: count string-typed ids across ALL raw entries.
    const idCounts = new Map();
    for (const raw of rawEntries) {
        if (
            raw &&
            typeof raw === "object" &&
            !Array.isArray(raw) &&
            typeof raw.id === "string" &&
            raw.id.length > 0
        ) {
            idCounts.set(raw.id, (idCounts.get(raw.id) || 0) + 1);
        }
    }
    const ambiguousIds = new Set();
    for (const [id, count] of idCounts) {
        if (count > 1) ambiguousIds.add(id);
    }

    // Step 2: validate each entry; drop if ambiguous OR invalid.
    const facts = [];
    for (const raw of rawEntries) {
        if (
            raw &&
            typeof raw === "object" &&
            !Array.isArray(raw) &&
            typeof raw.id === "string" &&
            ambiguousIds.has(raw.id)
        ) {
            continue; // ambiguous — excluded entirely
        }
        const validated = _validateFact(raw);
        if (validated === null) continue;
        facts.push(validated);
    }
    return { facts, ambiguousIds };
}

/**
 * Presentation state a UI should render for a capability. This is a
 * UI presentation hint — NEVER an execution permission.
 * ``presentActionUI: false`` is the safe render state. Even ``true``
 * requires the server to re-authorize the action at invocation
 * time.
 */
export function classifyForUi(fact) {
    // Shared strict validation MUST run first — no positive hint can
    // ever be returned for an invalid / malformed / partial fact.
    const validated = _validateFact(fact);
    if (validated === null) {
        return { presentActionUI: false, reason: "invalid_fact" };
    }
    if (validated.readiness !== "verified") {
        return { presentActionUI: false, reason: "readiness_not_verified" };
    }
    if (validated.enablement !== "not_held") {
        return { presentActionUI: false, reason: "enablement_not_not_held" };
    }
    if (validated.implementation_status !== "known_implemented") {
        return {
            presentActionUI: false,
            reason: "implementation_status_not_known_implemented",
        };
    }
    return { presentActionUI: true, reason: null };
}

/**
 * Map an HTTP status to a bounded generic error code. No body text
 * is ever incorporated in the returned error.
 */
function _httpErrorCode(status) {
    if (status === 401) return "http_401";
    if (status === 403) return "http_403";
    if (status === 404) return "http_404";
    if (typeof status === "number" && status >= 500 && status < 600) {
        return "http_5xx";
    }
    if (typeof status === "number" && status >= 400 && status < 500) {
        return "http_4xx";
    }
    return "http_unexpected_status";
}

/**
 * Result: { ok: true, contract } or { ok: false, error }.
 * ``ok: false`` MUST be treated as "no capabilities present a
 * positive action-UI hint" by consumers. ``error`` is ALWAYS a
 * bounded short code from a fixed set — never contains request
 * body / server detail / exception text / bearer / interpolated
 * user-supplied literals.
 *
 * Fixed error-code set:
 *   transport_not_injected
 *   session_not_injected
 *   session_error
 *   no_bearer
 *   transport_error
 *   transport_no_response
 *   http_401 | http_403 | http_404 | http_4xx | http_5xx | http_unexpected_status
 *   malformed_body
 *   unsupported_contract_version
 *   malformed_body_capabilities_not_array
 */
export async function fetchClientCapabilities({ transport, session }) {
    if (typeof transport !== "function") {
        return { ok: false, error: "transport_not_injected" };
    }
    if (!session || typeof session.getBearerToken !== "function") {
        return { ok: false, error: "session_not_injected" };
    }

    let bearer;
    try {
        bearer = await session.getBearerToken();
    } catch (_e) {
        // NEVER surface exception text — could contain secrets.
        return { ok: false, error: "session_error" };
    }
    if (typeof bearer !== "string" || bearer.length === 0) {
        return { ok: false, error: "no_bearer" };
    }

    let response;
    try {
        response = await transport({
            method: "GET",
            path: "/api/client/capabilities",
            headers: { Authorization: `Bearer ${bearer}` },
        });
    } catch (_e) {
        // NEVER surface transport exception text.
        return { ok: false, error: "transport_error" };
    }

    if (!response || typeof response !== "object") {
        return { ok: false, error: "transport_no_response" };
    }
    if (response.status !== 200) {
        // NEVER echo response.body.detail — could contain server
        // error text, provider names, or user-supplied literals.
        return { ok: false, error: _httpErrorCode(response.status) };
    }

    const body = response.body;
    if (!body || typeof body !== "object" || Array.isArray(body)) {
        return { ok: false, error: "malformed_body" };
    }
    if (!SUPPORTED_CONTRACT_VERSIONS.has(body.contract_version)) {
        // NEVER interpolate body.contract_version — server-supplied
        // string could contain arbitrary content.
        return { ok: false, error: "unsupported_contract_version" };
    }
    if (!Array.isArray(body.capabilities)) {
        return { ok: false, error: "malformed_body_capabilities_not_array" };
    }

    // Fail-closed collection validation. Ambiguous ids (any id
    // appearing more than once among raw entries with a string id)
    // are dropped ENTIRELY — both/all occurrences. This closes the
    // gap where a valid + malformed pair with the same id could
    // otherwise survive if the malformed twin were dropped
    // pre-count. Individually-invalid entries are also dropped.
    const { facts: capabilities } = _collectValidFacts(body.capabilities);

    return {
        ok: true,
        contract: {
            contract_version: body.contract_version,
            registry_version:
                typeof body.registry_version === "string"
                    ? body.registry_version
                    : null,
            generated_at:
                typeof body.generated_at === "string" ? body.generated_at : null,
            capabilities,
        },
    };
}

/**
 * Convenience index over a fetched contract.
 *
 * Returns a ``Map<string, Fact | null>``:
 *
 *   * Valid, unique id  → the validated fact.
 *   * Ambiguous id      → ``null`` (any id appearing more than once
 *                          among raw entries with a string id — the
 *                          fact occurrences themselves are excluded
 *                          from the index; ``Map.get(id) === null``
 *                          signals ambiguous lookup).
 *   * Absent id         → ``undefined`` (standard ``Map.get``
 *                          semantics).
 *   * Reserved / prototype keys (``__proto__``, ``constructor``,
 *     ``toString``, etc.) are stored on the ``Map`` as ordinary
 *     keys — they do NOT reach the object prototype chain, so
 *     prototype pollution is not reachable through this index.
 *
 * Facts that fail shared validation are excluded. Ambiguity is
 * detected on the RAW input BEFORE per-fact validation so a
 * valid+malformed same-id pair fails closed for BOTH occurrences.
 */
export function indexCapabilities(contract) {
    const idx = new Map();
    if (!contract || !Array.isArray(contract.capabilities)) return idx;
    const { facts, ambiguousIds } = _collectValidFacts(contract.capabilities);
    for (const f of facts) idx.set(f.id, f);
    // Documented convention: ambiguous ids retain a null entry in
    // the Map so callers doing a direct lookup see the fail-closed
    // signal explicitly (rather than getting ``undefined`` which
    // would be indistinguishable from "id not present").
    for (const id of ambiguousIds) idx.set(id, null);
    return idx;
}
